/**
 * Port of legacy `archive/.../lib/types.ts`. Keep field names byte-for-byte
 * identical so the backend Pydantic schemas line up.
 */
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

/**
 * Geography-true regions (PPP §B3).
 * @deprecated EMEA / ASIA / LATAM are kept for backward compat — remove in QQQ.
 */
export type Region =
  | "EUROPE"
  | "MIDDLE_EAST"
  | "AFRICA"
  | "NORTH_AMERICA"
  | "LATIN_AMERICA"
  | "ASIA_PACIFIC"
  | "EMEA"   // @deprecated
  | "ASIA"   // @deprecated
  | "LATAM"; // @deprecated

export type RenewalCycle = "1y" | "2y" | "3y" | "4y" | "5y" | "indefinite";

/** Static English fallback — use useRenewalCycleLabels() in components. */
export const RENEWAL_CYCLE_LABELS: Record<RenewalCycle, string> = {
  "1y": "Every 1 year",
  "2y": "Every 2 years",
  "3y": "Every 3 years",
  "4y": "Every 4 years",
  "5y": "Every 5 years",
  "indefinite": "Indefinite — no renewal required",
};

/** Hook version — returns translated labels for the current locale. */
export function useRenewalCycleLabels(): Record<RenewalCycle, string> {
  const { t } = useTranslation("cycles");
  return {
    "1y": t("1y"),
    "2y": t("2y"),
    "3y": t("3y"),
    "4y": t("4y"),
    "5y": t("5y"),
    "indefinite": t("indefinite"),
  };
}

export interface Country {
  id: string;
  name: string;
  region: Region;
  iso2: string;
  lat: number;
  lng: number;
  flag: string;
  _count?: { entities: number };
  // ── Service providers (migration 0005) ────────────────────────────────────
  lawyerTaxCorporate?: string | null;
  lawyerLabourHr?: string | null;
  litigationLawyer?: string | null;
  accountant?: string | null;
  statutoryAuditor?: string | null;
  banker?: string | null;
  debtCollectionAgency?: string | null;
  otherServiceProvider?: string | null;
  visaProvider?: string | null;
  healthcareProvider?: string | null;
  benefitsProvider?: string | null;
  payrollProvider?: string | null;
  // ── Tax guide ─────────────────────────────────────────────────────────────
  taxGuideFirm?: string | null;
  taxGuideLastUpdated?: string | null;
}

export interface Entity {
  id: string;
  countryId: string;
  country?: Country;
  region: Region;
  name: string;
  companyName: string | null;
  legalForm: string | null;
  registrationNo: string | null;
  vatNo: string | null;
  city: string | null;
  businessActivity: string | null;
  operationalAddress: string | null;
  registeredOffice: string | null;
  // Structured addresses (migration 0018) — optional like physicalOffice* below;
  // the API always returns them (null when unset).
  registeredAddressLine1?: string | null;
  registeredAddressLine2?: string | null;
  registeredCity?: string | null;
  registeredStateProvince?: string | null;
  registeredPostalCode?: string | null;
  registeredCountryCode?: string | null;
  operationalAddressLine1?: string | null;
  operationalAddressLine2?: string | null;
  operationalCity?: string | null;
  operationalStateProvince?: string | null;
  operationalPostalCode?: string | null;
  operationalCountryCode?: string | null;
  contact: string | null;
  ubo: string | null;
  uboDeclaration: string | null;
  director: string | null;
  directorTitle: string | null;
  signingAuthority: string | null;
  licence: string | null;
  issuingAuthority: string | null;
  licenceNo: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  // ── Typed fields (migration 0006) ─────────────────────────────────────────
  shareCapitalAmount?: number | null;
  shareCapitalCurrency?: string | null;
  parValueAmount?: number | null;
  parValueCurrency?: string | null;
  incorporationDateTyped?: string | null;
  eorObtainmentDateTyped?: string | null;
  leaseExpiryTyped?: string | null;
  fiscalYearEndMonth?: number | null;
  fiscalYearEndDay?: number | null;
  durationEnd?: string | null;
  durationIndefinite?: boolean | null;
  notes?: string | null;
  reviewFlag?: boolean;
  // ── Recovered / previously hidden fields ──────────────────────────────────
  officialRegister?: string | null;
  vatNoIntraEu?: string | null;
  ein?: string | null;
  gstNo?: string | null;
  taxResidenceCert?: string | null;
  // ── Autonomous-renewals anchor (migration 0009) ───────────────────────────
  agmDate?: string | null;
  // ── Physical office fields (migration 0016) ───────────────────────────────
  physicalOfficeRequired?: string | null;
  physicalOfficeType?: string | null;
  // ── Derived relations (present when backend includes them) ────────────────
  ownership_stakes?: OwnershipStake[];
  directors?: Director[];
  // ── Data integrity (PPP §D2) ──────────────────────────────────────────────
  /** Populated by backend audit cache; empty array when no gaps found. */
  dataIntegrityGaps?: string[];
}

export interface ChangeLog {
  id: string;
  entityId: string | null;      // nullable post-migration 0016
  entity?: {
    name: string;
    id?: string;
    country?: { name?: string | null; iso2?: string | null; flag?: string | null } | null;
  } | null;
  userId: string | null;
  user?: { name: string | null; email: string } | null;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  comment: string | null;
  createdAt: string;
  // Non-entity target scope (migration 0016)
  targetType?: string | null;   // "entity" | "country" | "user" | "regulatory"
  targetId?: string | null;
  targetName?: string | null;
}

export const CRITICAL_FIELDS: readonly string[] = [
  "ubo",
  "director",
];

/** Static English fallback — use useEntitySections() in components. */
// Address section fields, shared by the static + hook section definitions.
// Each free-text "original" legacy field is followed by its structured fields.
const ADDRESS_FIELDS: (keyof Entity)[] = [
  "registeredOffice",
  "registeredAddressLine1",
  "registeredAddressLine2",
  "registeredCity",
  "registeredStateProvince",
  "registeredPostalCode",
  "registeredCountryCode",
  "operationalAddress",
  "operationalAddressLine1",
  "operationalAddressLine2",
  "operationalCity",
  "operationalStateProvince",
  "operationalPostalCode",
  "operationalCountryCode",
  "contact",
  "physicalOfficeRequired",
  "physicalOfficeType",
];

export const ENTITY_SECTIONS: { title: string; fields: (keyof Entity)[]; sectionKey: string }[] = [
  {
    title: "Legal Identity",
    sectionKey: "legal_identity",
    fields: ["companyName", "legalForm", "registrationNo", "vatNo", "city", "businessActivity", "officialRegister"],
  },
  {
    title: "Tax & Registry",
    sectionKey: "tax_registry",
    fields: ["vatNoIntraEu", "ein", "gstNo", "taxResidenceCert"],
  },
  {
    title: "Address",
    sectionKey: "address",
    fields: ADDRESS_FIELDS,
  },
  {
    title: "Capital & Ownership",
    sectionKey: "capital_ownership",
    fields: ["ubo", "uboDeclaration"],
  },
  {
    title: "Directors",
    sectionKey: "directors",
    fields: [],
  },
  {
    title: "Licences & EOR",
    sectionKey: "licences_eor",
    fields: ["licence", "issuingAuthority", "licenceNo"],
  },
  {
    title: "Notes",
    sectionKey: "notes",
    fields: ["notes"],
  },
];

/** Hook version — returns section titles in the current locale. */
export function useEntitySections(): { title: string; fields: (keyof Entity)[]; sectionKey: string }[] {
  const { t } = useTranslation("entities");
  return [
    { title: t("section_legal_identity"),    sectionKey: "legal_identity",    fields: ["companyName", "legalForm", "registrationNo", "vatNo", "city", "businessActivity", "officialRegister"] },
    { title: t("section_tax_registry"),      sectionKey: "tax_registry",      fields: ["vatNoIntraEu", "ein", "gstNo", "taxResidenceCert"] },
    { title: t("section_address"),           sectionKey: "address",           fields: ADDRESS_FIELDS },
    { title: t("section_capital_ownership"), sectionKey: "capital_ownership", fields: ["ubo", "uboDeclaration"] },
    { title: t("section_directors"),         sectionKey: "directors",         fields: [] },
    { title: t("section_licences_eor"),      sectionKey: "licences_eor",      fields: ["licence", "issuingAuthority", "licenceNo"] },
    { title: t("section_notes"),             sectionKey: "notes",             fields: ["notes"] },
  ];
}

export const FIELD_LABELS: Partial<Record<keyof Entity, string>> & Record<string, string> = {
  companyName: "Company name",
  legalForm: "Legal form",
  registrationNo: "Registration no.",
  vatNo: "VAT no.",
  city: "City / Registry",
  businessActivity: "Business activity",
  officialRegister: "Official register",
  vatNoIntraEu: "VAT no. (intra-EU)",
  ein: "EIN",
  gstNo: "GST no.",
  taxResidenceCert: "Tax residence certificate",
  operationalAddress: "Operational address (original)",
  registeredOffice: "Registered address (original)",
  registeredAddressLine1: "Registered address line 1",
  registeredAddressLine2: "Registered address line 2",
  registeredCity: "Registered city",
  registeredStateProvince: "Registered state / province",
  registeredPostalCode: "Registered postal code",
  registeredCountryCode: "Registered country code",
  operationalAddressLine1: "Operational address line 1",
  operationalAddressLine2: "Operational address line 2",
  operationalCity: "Operational city",
  operationalStateProvince: "Operational state / province",
  operationalPostalCode: "Operational postal code",
  operationalCountryCode: "Operational country code",
  contact: "Contact",
  ubo: "UBO / Beneficial owner",
  uboDeclaration: "UBO declaration",
  director: "Director",
  directorTitle: "Title",
  signingAuthority: "Signing authority",
  licence: "Licence",
  issuingAuthority: "Issuing authority",
  licenceNo: "Licence no.",
  notes: "Notes",
  // ChangeLog lifecycle event labels (G5)
  entity_created: "Entity created",
  entity_deleted: "Entity deleted",
  entity_restored: "Entity restored",
  // Typed-fields audit labels (0006)
  shareCapitalAmount: "Share capital (amount)",
  shareCapitalCurrency: "Share capital (currency)",
  parValueAmount: "Par value (amount)",
  parValueCurrency: "Par value (currency)",
  incorporationDateTyped: "Incorporation date",
  eorObtainmentDateTyped: "EOR obtainment date",
  leaseExpiryTyped: "Lease expiry",
  fiscalYearEndMonth: "Fiscal year end (month)",
  fiscalYearEndDay: "Fiscal year end (day)",
  durationEnd: "Duration end date",
  durationIndefinite: "Indefinite duration",
  agmDate: "AGM date",
  // Director audit fields
  director_created: "Director added",
  director_deleted: "Director removed",
  "director.name": "Director name",
  "director.title": "Director title",
  "director.signingAuthority": "Director signing authority",
  // Obligation / ownership audit fields
  obligation_created: "Obligation created",
  obligation_deleted: "Obligation deleted",
  mark_renewed: "Mark renewed",
  ownership_stake: "Ownership stake",
  "obligation.status": "Obligation status",
  "obligation.nextDueDate": "Obligation next due date",
  // Autonomous-renewals obligation recompute
  obligation_recomputed: "Obligation recomputed",
  obligation_confirmed: "Obligation confirmed",
  // Migration 0016 fields
  physicalOfficeRequired: "Physical office required",
  physicalOfficeType: "Physical office type",
  name: "Entity name",
  region: "Region",
  countryId: "Country",
  director_backfill_split: "Director data migrated",
  country_created: "Country created",
  user_role: "User role changed",
  regulatory_confirmed: "Regulatory candidate confirmed",
  regulatory_dismissed: "Regulatory candidate dismissed",
  regulatory_applied: "Regulatory change applied",
};

/** Returns a translated field label. Reads i18n.language when lang is not passed. */
export function getFieldLabel(field: keyof Entity | string, _lang?: "en" | "fr"): string {
  // t() is not available outside React; use the i18n instance directly.
  return i18n.t(`fields:${String(field)}`, { defaultValue: FIELD_LABELS[field as keyof Entity] ?? String(field) });
}

/** Render-only. No-op pass-through kept for InlineEdit compatibility. */
export function formatFieldValue(_field: string, value: string): string {
  return value;
}

// ─── Obligation model (typed-renewal-model refactor) ─────────────────────────

export type ObligationCycle = "1y" | "2y" | "3y" | "4y" | "5y" | "indefinite";
export type ObligationStatus2 =
  | "confirmed"
  | "estimated"
  | "needs_confirmation"
  | "indefinite"
  | "not_required";
export type ObligationCategory = "license" | "company" | "annual_filing" | "eor" | "other";

// ─── Obligation schedule types (autonomous-renewals, migration 0009) ─────────

export type AnchorRule =
  | "anniversary"
  | "fy_end_offset"
  | "post_event_offset"
  | "fixed_date"
  | "fixed_window"
  | "state_variable"
  | "none";

export type OffsetUnit = "days" | "months";
export type CycleKind = "simple" | "indefinite" | "manual" | "phased";

export interface AnchorSpec {
  rule: AnchorRule;
  offsetValue?: number | null;
  offsetUnit?: OffsetUnit | null;
  rangeMaxValue?: number | null;
  fixedMonth?: number | null;
  fixedDay?: number | null;
}

export interface PhasedEntry {
  durationYears: number;
  cycle: string;
}

export interface CycleSpec {
  kind: CycleKind;
  cycle?: string | null;
  phases?: PhasedEntry[] | null;
  then?: CycleSpec | null;
}

export interface SecondaryFiling {
  /** Stable id persisted in schedule.secondary — assigned on first reconcile. Undefined on new (unsaved) filings. */
  id?: string | null;
  name: string;
  /** Standard cycle string: "1y"–"5y" / "indefinite". Null when the interval path is used. */
  cycle?: string | null;
  /** Custom interval numerator — e.g. 3 (quarterly). Used when cycle is null. */
  intervalNum?: number | null;
  intervalUnit?: "months" | "years" | null;
  /** Per-filing anchor override. Null = inherit parent anchor. */
  anchor?: AnchorSpec | null;
  referenceNote: string;
}

export interface ObligationSchedule {
  anchor: AnchorSpec;
  cycle: CycleSpec;
  effectiveFrom?: string | null;
  referenceNote: string;
  secondary: SecondaryFiling[];
}
export type IntervalUnit = "months" | "years";

export interface ObligationCompletion {
  id: string;
  obligationId: string;
  completedDate: string;
  previousNextDue: string | null;
  newNextDue: string | null;
  cycleAtCompletion: string | null;
  comment: string | null;
  userId: string | null;
  /** Resolved from completion.user.name by the backend (Option A per DLD §5.2). */
  userName: string | null;
  createdAt: string;
}

export interface Obligation {
  id: string;
  entityId: string;
  name: string;
  category: ObligationCategory;
  nextDueDate: string | null;
  cycle: ObligationCycle | string | null;
  intervalNum: number | null;
  intervalUnit: IntervalUnit | null;
  effectiveFrom: string | null;
  status: ObligationStatus2;
  notes: string | null;
  reviewFlag: boolean;
  completions: ObligationCompletion[];
  createdAt: string;
  updatedAt: string;
  // Autonomous-renewals additions (migration 0009)
  schedule?: ObligationSchedule | null;
  explanation?: string | null;
  referenceNote?: string | null;
  /** Set on child (secondary-filing) obligations. Links back to the parent obligation UUID. */
  parentObligationId?: string | null;
}

export interface ObligationsResponse {
  obligations: Obligation[];
}

export interface ObligationCreate {
  name: string;
  category: ObligationCategory;
  nextDueDate?: string | null;
  cycle?: ObligationCycle | null;
  intervalNum?: number | null;
  intervalUnit?: IntervalUnit | null;
  effectiveFrom?: string | null;
  status: ObligationStatus2;
  notes?: string | null;
  reviewFlag?: boolean;
}

export interface ObligationPatch {
  name?: string;
  nextDueDate?: string | null;
  cycle?: ObligationCycle | string | null;
  intervalNum?: number | null;
  intervalUnit?: IntervalUnit | null;
  effectiveFrom?: string | null;
  status?: ObligationStatus2;
  notes?: string | null;
  reviewFlag?: boolean | null;
  comment?: string | null;
  schedule?: ObligationSchedule | null;
}

export interface MarkObligationRenewedParams {
  completedDate: string;
  expectedCurrentNextDue?: string | null;
  comment?: string | null;
}

// ─── Director model ───────────────────────────────────────────────────────────

export interface Director {
  id: string;
  entityId: string;
  seq: number;
  name: string;
  title: string | null;
  signingAuthority: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DirectorsResponse {
  directors: Director[];
  total: number;
}

export interface DirectorCreate {
  name: string;
  title?: string | null;
  signingAuthority?: string | null;
  seq?: number;
  comment?: string | null;
}

export interface DirectorPatch {
  name?: string | null;
  title?: string | null;
  signingAuthority?: string | null;
  seq?: number | null;
  comment?: string | null;
  expectedUpdatedAt?: string | null;
}

// ─── Ownership model ──────────────────────────────────────────────────────────

export interface OwnershipStake {
  id: string;
  entityId: string;
  seq: number;
  ownerName: string;
  percentage: number | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OwnershipResponse {
  stakes: OwnershipStake[];
  percentageSum: number | null;
  reviewFlag: boolean;
}

export interface OwnershipStakeCreate {
  ownerName: string;
  percentage?: number | null;
  note?: string | null;
  seq?: number;
  comment: string | null;
}

export interface OwnershipStakePatch {
  ownerName?: string;
  percentage?: number | null;
  note?: string | null;
  seq?: number;
  comment: string | null;
  /** ISO timestamp for optimistic-lock check — backend returns 409 if stale. */
  expectedUpdatedAt?: string;
}

// ─── Typed entity fields (new columns added in migration 0006) ────────────────

export interface TypedEntityFields {
  shareCapitalAmount?: number | null;
  shareCapitalCurrency?: string | null;
  parValueAmount?: number | null;
  parValueCurrency?: string | null;
  incorporationDateTyped?: string | null;
  eorObtainmentDateTyped?: string | null;
  leaseExpiryTyped?: string | null;
  fiscalYearEndMonth?: number | null;
  fiscalYearEndDay?: number | null;
  durationEnd?: string | null;
  durationIndefinite?: boolean | null;
  agmDate?: string | null;
  notes?: string | null;
  reviewFlag?: boolean;
  comment: string | null;
}

/** Fields whose save must invalidate obligation/renewal-status queries. */
export const ANCHOR_FIELDS: ReadonlySet<string> = new Set([
  "incorporationDateTyped",
  "fiscalYearEndMonth",
  "fiscalYearEndDay",
  "eorObtainmentDateTyped",
  "agmDate",
]);

/** What the API actually emits — matches backend Literal["red","orange","green","none"]. */
export type RenewalEventSeverity = "red" | "orange" | "green" | "none";

/** Internal calendar-layout buckets (used only by renewalLayout.ts — not an API value). */
export type CalendarBucketSeverity = "expired" | "le30" | "le60" | "le90";

/** A renewal event surfaced by `/api/renewals`. */
export interface RenewalEvent {
  id: string;
  entityId: string;
  entityName: string;
  country: string;
  countryFlag: string;
  region: Region;
  type: "License" | "Company" | "AnnualFiling" | "EOR";
  rawValue: string;
  parsedDate: string;
  /** What the API actually emits — matches backend Literal["red","orange","green","none"]. */
  severity: RenewalEventSeverity;
  daysUntil: number;
  derived?: boolean;
  /** Set when this event originates from a child (secondary filing) obligation. */
  parentObligationId?: string | null;
  /** Human-readable name of the parent obligation (for feed grouping context). */
  parentName?: string | null;
}

/** Agent chat shapes. */
export interface ChatMessage {
  role: "system" | "user" | "assistant" | "tool";
  content: string;
}
export interface EntityChip {
  id: string;
  name: string;
  country?: string | null;
  flag?: string | null;
  region?: string | null;
}
export interface AgentResponse {
  intent: "rag_chat";
  answerText: string;
  entities: EntityChip[];
  suggestions?: string[];
  lang: "fr" | "en";
}

/** Entity with its audit changelog — returned by admin/entity-detail endpoints. */
export interface EntityWithChanges extends Entity {
  changes: ChangeLog[];
  deletedAt?: string | null;
}

/** Admin shapes. */
export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  role: import("./permissions").Role;
  createdAt: string;
}

/**
 * Renewal obligation status matrix — shapes returned by GET /entities/{id}/renewal-status.
 * The backend adapter maps: confirmed→real, needs_confirmation→awaiting_data,
 * indefinite/not_required→not_applicable.
 * Field names are snake_case to match FastAPI's JSON output directly (no transformation layer).
 */
export type ObligationStatus =
  | "confirmed"
  | "real"
  | "awaiting_data"
  | "not_applicable";

export type ObligationKey = "license" | "company" | "annualFiling" | "eor";

export interface RenewalStatusRow {
  obligation: ObligationKey;
  /** Obligation UUID — present when an obligation row has a linked Obligation record. */
  obligation_id?: string | null;
  cycle: string | null;
  cycle_label: string | null;
  next_due: string | null;
  status: ObligationStatus;
  reason: string | null;
  /** Set only on child rows. Links back to the parent obligation UUID. */
  parentObligationId?: string | null;
  /** Human-readable name for the row. Set on child rows (secondary filing name); may also be set on parent rows. */
  name?: string | null;
}

export interface RenewalStatusResponse {
  entity_id: string;
  country: string;
  rows: RenewalStatusRow[];
}

// ─── Regulatory Watch types (migration 0010) ──────────────────────────────────

export type RegwatchDomain =
  | "renewal_obligation"
  | "ownership_ubo"
  | "share_capital"
  | "directors_officers"
  | "licensing_permits"
  | "tax_vat_registration"
  | "corporate_form"
  | "employment_eor"
  | "registered_office_lease"
  | "reporting_substance"
  | "entity_registry"
  | "personal_income_tax_rate"
  | "social_security_contribution_rate"
  | "other";

export type RegwatchApplyMode = "obligation" | "advisory";
export type RegwatchChangeKind = "cycle" | "deadline" | "eor_rule" | "new_filing";
export type RegwatchConfidence = "low" | "med" | "high";
export type RegwatchStatus = "new" | "under_review" | "confirmed" | "dismissed" | "applied";

export interface RegistryOfficer {
  name: string;
  role: string;
  appointedDate?: string | null;
  sourceQuote: string;
}

export interface RegistryDueDate {
  description: string;
  dueDate?: string | null;
  overdue: boolean;
  sourceQuote: string;
}

export interface EntityRegistrySnapshot {
  companyStatus?: string | null;
  companyStatusQuote?: string | null;
  registeredOffice?: string | null;
  registeredOfficeQuote?: string | null;
  officers: RegistryOfficer[];
  dueDates: RegistryDueDate[];
  otherNotableItems?: string | null;
  summary: string;
  primarySourceQuote: string;
  effectiveDate: string;
  confidence: RegwatchConfidence;
}

export interface RuleChangeCandidate {
  id: string;
  countryId: string;
  domain: RegwatchDomain;
  applyMode: RegwatchApplyMode;
  obligationCategory: string | null;
  changeKind: RegwatchChangeKind | null;
  summary: string;
  sourceQuote: string;
  sourceUrl: string;
  sourceName: string | null;
  publishedDate: string;
  detectedDate: string;
  confidence: RegwatchConfidence;
  status: RegwatchStatus;
  affectedEntityIds: string[];
  reviewer: string | null;
  reviewNotes: string | null;
  linkedChangelogId: string | null;
  createdAt: string;
  updatedAt: string;
  entityRegistrySnapshot?: EntityRegistrySnapshot | null;
  /** Effective date stated on the source page, when present (P0). */
  effectiveDate: string | null;
  /** Whether the current signed-in user has marked this candidate reviewed (per-person). */
  reviewed: boolean;
}

export interface RegulatorySource {
  id: string;
  countryId: string;
  name: string;
  url: string;
  type: "registry" | "gazette" | "regulator" | "other";
  active: boolean;
  addedBy: string | null;
  lastCheckedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  entityIds?: string[] | null;
  // ── Source health (P0 — migration 0020) ────────────────────────────────────
  monitoringMode: "auto" | "manual";
  manualReason: string | null;
  lastFetchStatus: "ok" | "failed" | null;
  lastFetchBytes: number | null;
  lastSuccessfulReadAt: string | null;
  consecutiveFailures: number;
}

// ─── Entity Documents (migration 0011) ───────────────────────────────────────

export interface Document {
  id: string;
  entityId: string;
  /** User-given display name — user content, never translated. */
  name: string;
  /** Optional summary — user content, never translated. */
  notes: string | null;
  /** Sanitized original filename — for display only. */
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
  uploadedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentListResponse {
  documents: Document[];
}

/** Hard cap enforced on both client and server (config.py DOCUMENTS_MAX_SIZE_MB). */
export const DOCUMENTS_MAX_SIZE_MB = 50;

/** Allowed upload extensions — mirrors config.py DOCUMENTS_ALLOWED_EXTENSIONS. */
export const DOCUMENTS_ALLOWED_EXTENSIONS = new Set([
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
  ".txt",
  ".png",
  ".jpg",
  ".jpeg",
]);

/** Subset of contentType values the backend will serve with
 *  Content-Disposition: inline. Must stay in sync with backend
 *  INLINE_SAFE_CONTENT_TYPES (document_storage.py:29). */
export const INLINE_SAFE_CONTENT_TYPES: ReadonlySet<string> = new Set([
  "application/pdf",
  "image/png",
  "image/jpeg",
]);

export function isInlineSafe(contentType: string | null | undefined): boolean {
  return !!contentType && INLINE_SAFE_CONTENT_TYPES.has(contentType);
}

// ───────────────────────────────────────────────────────────────────────────
// Admin invitations
// ───────────────────────────────────────────────────────────────────────────

export type InvitationStatus = "pending" | "accepted" | "revoked" | "expired";

export interface Invitation {
  id: string;
  email: string;
  role: import("./permissions").Role;
  invitedById: string;
  invitedByName: string | null;
  invitedAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  acceptedByUserId: string | null;
  revokedAt: string | null;
  revokedById: string | null;
  lastSentAt: string;
  status: InvitationStatus;
  personalMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvitationListResponse {
  pending: Invitation[];
  accepted: Invitation[];
  revoked: Invitation[];
  expired: Invitation[];
}

export interface InvitationCreatePayload {
  email: string;
  role: import("./permissions").Role;
  personalMessage?: string | null;
}
